import { injectable } from 'inversify';

import {
  DashboardGridItem,
  SectionKey,
  Widget,
  WidgetDropTarget,
  WidgetType,
} from '../../../types';

import { DashboardGridService } from './dashboardGrid.service';

@injectable()
export class DashboardSectionsService {
  constructor(private readonly gridService: DashboardGridService) {}

  /** Определяет активную секцию по типу виджета */
  getSectionForWidgetType(type?: WidgetType): SectionKey {
    if (type === 'quick_actions') return 'top';
    if (type === 'posts_feed') return 'posts';
    return 'main';
  }

  /**
   * Изменяет размер виджета, при необходимости вытесняя нижние виджеты
   * Возвращает обновлённый массив виджетов секции с пересчитанными x, y
   */
  resizeWidget(
    sectionWidgets: Widget[],
    widgetId: string,
    newSize: number,
    cols: number,
  ): Widget[] {
    const widget = sectionWidgets.find((w) => w.id === widgetId);
    if (!widget) return sectionWidgets;

    const updatedWidget = {
      ...widget,
      size: newSize,
      x: Math.min(widget.x ?? 0, cols - newSize),
    };

    const grid = this.gridService.buildGridFromPositions(sectionWidgets, cols);
    const tempGrid = this.gridService.compactGrid(
      this.gridService.getGridWithoutWidget(grid, widgetId),
      cols,
    );

    const targetIndex = (updatedWidget.y ?? 0) * cols + (updatedWidget.x ?? 0);
    const newGrid = this.gridService.shiftAndInsertCard(
      tempGrid,
      updatedWidget,
      targetIndex,
      cols,
    );

    return this.gridService.updateWidgetPositions(newGrid, cols);
  }

  /** Вставляет новый виджет в секцию по указанному таргету и возвращает обновлённые виджеты */
  addWidgetToSection(
    sectionWidgets: Widget[],
    widget: Widget,
    target: WidgetDropTarget,
    cols: number,
  ): Widget[] {
    const grid = this.gridService.buildGridFromPositions(sectionWidgets, cols);
    let newGrid: DashboardGridItem[];

    if (target.type === 'cell') {
      if (grid[target.index]?.type === 'occupied') {
        newGrid = this.gridService.shiftAndInsertCard(
          grid,
          widget,
          target.index,
          cols,
        );
      } else {
        const dropIndex = this.gridService.findNearestDropPosition(
          target.index,
          grid,
          cols,
          widget.size,
        );
        const cursorRow = Math.floor(target.index / cols);
        const dropRow = Math.floor(dropIndex / cols);

        if (dropRow > cursorRow) {
          const adjustedIndex = this.gridService.clampIndexInRow(
            target.index,
            cols,
            widget.size,
          );
          newGrid = this.gridService.shiftAndInsertCard(
            grid,
            widget,
            adjustedIndex,
            cols,
          );
        } else {
          newGrid = this.gridService.insertWidgetAt(
            grid,
            widget,
            dropIndex,
            cols,
          );
        }
      }
    } else if (target.type === 'widget') {
      const widgetIndex = this.gridService.findWidgetIndex(
        grid,
        target.widgetId,
      );
      if (widgetIndex !== -1) {
        newGrid = this.gridService.shiftAndInsertCard(
          grid,
          widget,
          widgetIndex,
          cols,
        );
      } else {
        newGrid = this.gridService.normalizeGrid(
          [...sectionWidgets, widget],
          cols,
        );
      }
    } else {
      newGrid = this.gridService.normalizeGrid(
        [...sectionWidgets, widget],
        cols,
      );
    }

    return this.gridService.updateWidgetPositions(newGrid, cols);
  }

  /** Перемещает виджет на указанную ячейку. Возвращает null, если перемещение недопустимо */
  moveWidgetToCell(
    sectionWidgets: Widget[],
    widgetId: string,
    targetIndex: number,
    cols: number,
  ): Widget[] | null {
    const widget = sectionWidgets.find((w) => w.id === widgetId);
    if (!widget) return null;

    const grid = this.gridService.buildGridFromPositions(sectionWidgets, cols);
    const tempGrid = this.gridService.compactGrid(
      this.gridService.getGridWithoutWidget(grid, widgetId),
      cols,
    );

    if (tempGrid[targetIndex]?.type === 'occupied') {
      const newGrid = this.gridService.shiftAndInsertCard(
        tempGrid,
        widget,
        targetIndex,
        cols,
      );
      return this.gridService.updateWidgetPositions(newGrid, cols);
    }

    const dropIndex = this.gridService.findNearestDropPosition(
      targetIndex,
      tempGrid,
      cols,
      widget.size,
    );
    const cursorRow = Math.floor(targetIndex / cols);
    const dropRow = Math.floor(dropIndex / cols);

    if (dropRow > cursorRow) {
      const adjustedIndex = this.gridService.clampIndexInRow(
        targetIndex,
        cols,
        widget.size,
      );
      const newGrid = this.gridService.shiftAndInsertCard(
        tempGrid,
        widget,
        adjustedIndex,
        cols,
      );
      return this.gridService.updateWidgetPositions(newGrid, cols);
    }

    const newGrid = this.gridService.insertWidgetAt(
      tempGrid,
      widget,
      dropIndex,
      cols,
    );

    const originalIds = this.gridService.getWidgetIds(tempGrid);
    const resultIds = this.gridService.getWidgetIds(newGrid);
    const allPreserved = originalIds.every((id) => resultIds.includes(id));
    if (!allPreserved) return null;

    return this.gridService.updateWidgetPositions(newGrid, cols);
  }

  /** Перемещает виджет на позицию другого виджета со сдвигом при необходимости */
  moveWidgetToWidgetPosition(
    sectionWidgets: Widget[],
    activeWidgetId: string,
    overWidgetId: string,
    cols: number,
  ): Widget[] {
    const activeWidget = sectionWidgets.find((w) => w.id === activeWidgetId);
    if (!activeWidget) return sectionWidgets;

    const grid = this.gridService.buildGridFromPositions(sectionWidgets, cols);
    const tempGrid = this.gridService.compactGrid(
      this.gridService.getGridWithoutWidget(grid, activeWidgetId),
      cols,
    );

    const targetIndex = this.gridService.findWidgetIndex(
      tempGrid,
      overWidgetId,
    );
    if (targetIndex === -1) return sectionWidgets;

    const dropIndex = this.gridService.findNearestDropPosition(
      targetIndex,
      tempGrid,
      cols,
      activeWidget.size,
    );
    const cursorRow = Math.floor(targetIndex / cols);
    const dropRow = Math.floor(dropIndex / cols);

    let newGrid: DashboardGridItem[];

    if (dropRow <= cursorRow) {
      newGrid = this.gridService.insertWidgetAt(
        tempGrid,
        activeWidget,
        dropIndex,
        cols,
      );
      const originalIds = this.gridService.getWidgetIds(tempGrid);
      const resultIds = this.gridService.getWidgetIds(newGrid);
      const allPreserved = originalIds.every((id) => resultIds.includes(id));
      if (!allPreserved) {
        newGrid = this.gridService.shiftAndInsertCard(
          tempGrid,
          activeWidget,
          targetIndex,
          cols,
        );
      }
    } else {
      newGrid = this.gridService.shiftAndInsertCard(
        tempGrid,
        activeWidget,
        targetIndex,
        cols,
      );
    }

    return this.gridService.updateWidgetPositions(newGrid, cols);
  }

  /** Переставляет виджет в рамках секции */
  reorderWidgets(
    sectionWidgets: Widget[],
    activeWidgetId: string,
    overWidgetIdOrEnd: string | -1,
    cols: number,
  ): Widget[] {
    const oldIndex = sectionWidgets.findIndex((w) => w.id === activeWidgetId);
    if (oldIndex === -1) return sectionWidgets;

    let newIndex: number;
    if (overWidgetIdOrEnd === -1) {
      newIndex = sectionWidgets.length - 1;
    } else {
      const overIndex = sectionWidgets.findIndex(
        (w) => w.id === overWidgetIdOrEnd,
      );
      if (overIndex === -1) return sectionWidgets;
      newIndex = overIndex;
    }

    const reordered = [...sectionWidgets];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);

    const newGrid = this.gridService.normalizeGrid(reordered, cols);
    return this.gridService.updateWidgetPositions(newGrid, cols);
  }

  /** Удаляет виджет из секции и возвращает обновлённые виджеты */
  removeWidgetFromSection(
    sectionWidgets: Widget[],
    widgetId: string,
    cols: number,
  ): Widget[] {
    const grid = this.gridService.buildGridFromPositions(sectionWidgets, cols);
    const newGrid = this.gridService.removeWidgetAt(
      grid,
      this.gridService.findWidgetIndex(grid, widgetId),
      cols,
    );
    return this.gridService.updateWidgetPositions(newGrid, cols);
  }

  /** Возвращает индекс начала последней полностью пустой строки для коллапса */
  findCollapsedFromIndex(
    displayGrid: DashboardGridItem[],
    cols: number,
    shiftTargetRow: number,
  ): number {
    if (shiftTargetRow === -1) return -1;

    for (let row = Math.floor(displayGrid.length / cols) - 1; row >= 0; row--) {
      const rowStart = row * cols;
      const allEmpty = displayGrid
        .slice(rowStart, rowStart + cols)
        .every((item) => item.type === 'empty');
      if (allEmpty) {
        return rowStart;
      }
    }

    return -1;
  }
}
